import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants/constants';
import { TableColumnInterface } from '../../../interfaces/tableColumn.interface';
import useGetTokenSessions from '../../../apiHooks/queries/authTokens/useGetTokenSessions';
import { useDeleteSessionToken } from '../../../apiHooks/mutations/authTokens/tokenMutations';
import TokensTable from '../../../components/tables/tokensTable/tokensTable';
import ConfirmDialog from '../../../dialogs/confirmDialog';
import DateTimeCell from '../../../partials/dateTimeCell';
import IdTooltipCell from '../../../partials/idTooltipCell';
import PhoneIcon from '../../../assets/icons/phone.svg';
import DesktopIcon from '../../../assets/icons/desktop.svg'
import tabletIcon from '../../../assets/icons/tablet.svg';
import terminalIcon from '../../../assets/icons/terminal.svg';
import robotIcon from '../../../assets/icons/robot.svg';
import unknownIcon from '../../../assets/icons/unknown.svg';

const columns: TableColumnInterface[] = [ {
  field: 'deviceDetail',
  headerName: 'deviceDetail',
  cellRenderer: (_: string, row: any) => {
    const getDeviceIcon = (type: string) => {
      const deviceType = type?.trim()?.toLowerCase();

      switch (deviceType) {
        case 'mobile':
          return <img src={PhoneIcon} width={18} height={18} alt="Mobile" />;
        case 'desktop':
          return <img src={DesktopIcon} width={20} height={20} alt="Desktop" />;
        case 'tablet':
          return <img src={tabletIcon} width={20} height={20} alt="Tablet" />;
        case 'terminal':
        case 'robot':
          return <img src={terminalIcon} width={20} height={20} alt="Terminal" />;
        default:
          return null;
      }
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {getDeviceIcon(row.deviceType)}
        <span>{row.deviceDetail}</span>
      </div>
    );
  },
},
  { field: 'ipAddress', headerName: 'ipAddress', cellRenderer: (params: string) => <IdTooltipCell params={params} /> },
  { field: 'lastUsedIp', headerName: 'lastUsedIp', cellRenderer: (params: string) => <IdTooltipCell params={params} /> },
  { field: 'createdAt', headerName: 'createdAt', cellRenderer: (params: string) => <DateTimeCell params={params} /> },
  { field: 'lastUsedAt', headerName: 'lastUsedAt', cellRenderer: (params: string) => <DateTimeCell params={params} /> },
  { field: 'expiresAt', headerName: 'expiresAt', cellRenderer: (params: string) => <DateTimeCell params={params} /> },

];


const TokenSessionsTab = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTokenId, setSelectedTokenId] = useState<string | number | null>(null);
  const [searchParams] = useSearchParams();
  const { mutateAsync: deleteSessionToken, isLoading: isDeleting } = useDeleteSessionToken();

  const [searchInput, setSearchInput] = useState({
    search: searchParams.get('search') || '',
  });

  useEffect(() => {
    setPage(0);
  }, [searchInput.search, rowsPerPage]);

  const handleDeleteClick = (token: any) => {
    setSelectedTokenId(token?.id ?? null);
    setIsDeleteDialogOpen(true);
  };

  const handleOnDelete = async () => {
    if (selectedTokenId == null) return;
    await deleteSessionToken(selectedTokenId);
    setIsDeleteDialogOpen(false);
    setSelectedTokenId(null);
    refetch();
  };

  const {
    data: sessionsData,
    isError,
    isFetching,
    refetch,
    error,
  } = useGetTokenSessions(
    'TOKENS_SESSIONS',
    {
      search: searchInput.search,
      rows: rowsPerPage,
      page: page + 1,
    },
  );

  return (
    <>
      <ConfirmDialog
        open={isDeleteDialogOpen}
        variant="warning"
        title="deleteToken"
        text="deleteTokenConfirm"
        submitButtonText="delete"
        isDisabled={isDeleting}
        onCloseDialog={() => {
          setIsDeleteDialogOpen(false);
          setSelectedTokenId(null);
        }}
        onSubmit={handleOnDelete}
      />
      <TokensTable
        title="tokensSessions"
        columns={columns}
        rows={sessionsData?.data ?? []}
        total={sessionsData?.meta?.total ?? 0}
        page={page}
        rowsPerPage={rowsPerPage}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        isError={isError}
        isFetching={isFetching}
        isDeleting={isDeleting}
        refetch={refetch}
        search={searchInput.search}
        setSearch={(search) => setSearchInput({ search })}
        onDelete={handleDeleteClick}
      />
    </>
  );
};

export default TokenSessionsTab;
